import type { FieldOption } from '../components/SearchableSelect'

// Candidatos de `campos_externos.key` por concepto: el upstream de cada cliente
// puede nombrar el campo de forma distinta; probamos en orden de preferencia.
export const CAMPO_CADENA_CANDIDATES = ['cadena', 'farmacia', 'codigo_farmacia']
export const CAMPO_REPRESENTANTE_CANDIDATES = ['representante', 'rep', 'gerente_zona']

/**
 * Llave normalizada de un nombre de persona, **orden-independiente**: minúsculas
 * (locale es), sin acentos/diacríticos, espacios colapsados y tokens ordenados
 * alfabéticamente. Así "CESAR GAVIÑO LAVALLE" y "GAVIÑO LAVALLE CESAR" producen
 * la misma llave, tolerando que las distintas fuentes usen orden
 * nombres-apellidos vs apellidos-nombres. Devuelve '' si no hay tokens.
 *
 * Usado para emparejar el `representante_nombre` del upstream contra el catálogo
 * estático de representantes (ver `representantesMedifarmaPeru.ts`); el generador
 * del catálogo usa esta misma función para que ambas llaves coincidan.
 */
export function normalizarNombreClave(nombre: string | null | undefined): string {
  if (!nombre) return ''
  return String(nombre)
    .toLocaleLowerCase('es')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9ñ\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ')
}

/** Primer campo `agrupable: true` que coincida con alguno de los candidatos. */
export function pickCampo(
  campos: Array<{ key: string; agrupable: boolean; label: string }> | undefined,
  candidates: string[],
): { key: string; label: string } | null {
  if (!campos) return null
  for (const cand of candidates) {
    const found = campos.find((c) => c.key === cand && c.agrupable)
    if (found) return { key: found.key, label: found.label }
  }
  return null
}

/** Primer campo `filtrable: true` que coincida con alguno de los candidatos. */
export function pickCampoFiltrable(
  campos: Array<{ key: string; filtrable: boolean; label: string }> | undefined,
  candidates: string[],
): { key: string; label: string } | null {
  if (!campos) return null
  for (const cand of candidates) {
    const found = campos.find((c) => c.key === cand && c.filtrable)
    if (found) return { key: found.key, label: found.label }
  }
  return null
}

/**
 * Extrae pares {value: id, label: nombre} únicos desde el listado de participantes.
 * Los endpoints de métricas sólo devuelven el nombre resuelto, pero el upstream
 * filtra por ID — por eso necesitamos los pares para que el dropdown muestre el
 * nombre y envíe el ID.
 *
 * El upstream a veces devuelve varios IDs y/o variantes de formato (espacios
 * sobrantes, mayúsculas, acentos) para una misma cadena, lo que generaba opciones
 * duplicadas. Estandarizamos el nombre; con `dedupePorNombre` (sólo cadena)
 * deduplicamos por nombre normalizado y conservamos un ID representativo estable
 * (el menor). En representante NO se dedupe por nombre: dos personas distintas
 * pueden llamarse igual.
 */
export function collectPairs(
  participantes: Array<Record<string, unknown>>,
  getId: (p: Record<string, unknown>) => unknown,
  getNombre: (p: Record<string, unknown>) => unknown,
  dedupePorNombre = false,
): FieldOption[] {
  const limpiar = (s: string) => s.trim().replace(/\s+/g, ' ')
  const clave = (s: string) =>
    limpiar(s).toLocaleLowerCase('es').normalize('NFD').replace(/[̀-ͯ]/g, '')
  const esMenor = (a: string, b: string) => {
    const na = Number(a)
    const nb = Number(b)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na < nb
    return a.localeCompare(b) < 0
  }

  const seen = new Map<string, { value: string; label: string }>()
  for (const p of participantes) {
    const id = getId(p)
    if (id === undefined || id === null || String(id).trim() === '') continue
    const value = String(id).trim()
    const nombre = getNombre(p)
    const label = nombre ? limpiar(String(nombre)) : value
    const key = dedupePorNombre ? clave(label) : value
    const prev = seen.get(key)
    if (!prev) seen.set(key, { value, label })
    else if (dedupePorNombre && esMenor(value, prev.value)) {
      seen.set(key, { value, label: prev.label })
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
  )
}

/**
 * Construye opciones de dropdown para **todos** los campos de texto filtrables
 * (`campos_externos` con `filtrable: true` y `tipo === 'string'`) que aún no
 * tienen opciones explícitas — provincia/cadena/representante/producto se arman
 * aparte con su propia lógica de ID/nombre.
 *
 * Los valores únicos se derivan del listado cargado (participantes): primero se
 * busca la llave en `datos_extra[key]` (con `datos_extra[key]_nombre` como
 * etiqueta si existe) y, si no está, se cae al valor top-level `p[key]`. NO se
 * deduplica por nombre normalizado: cada valor distinto es una opción propia para
 * que la selección coincida **exactamente** con lo almacenado (el input de texto
 * libre exigía esa coincidencia exacta y por eso "no filtraba"; una campo como
 * "Razón social" se vuelve una lista desplegable con valores reales).
 *
 * `yaCubiertos` son las llaves que el llamador ya resolvió (provincia, cadena,
 * representante, producto) para no pisarlas.
 */
export function autoFieldOptions(
  participantes: Array<Record<string, unknown>>,
  campos: Array<{ key: string; filtrable: boolean; tipo: string }> | undefined,
  yaCubiertos: string[],
): Record<string, FieldOption[]> {
  const out: Record<string, FieldOption[]> = {}
  const cubiertos = new Set(yaCubiertos)
  for (const c of campos ?? []) {
    if (!c.filtrable || c.tipo !== 'string' || cubiertos.has(c.key)) continue
    const k = c.key
    const opciones = collectPairs(
      participantes,
      (p) => {
        const e = p.datos_extra as Record<string, unknown> | undefined
        return e && k in e ? e[k] : p[k]
      },
      (p) => {
        const e = p.datos_extra as Record<string, unknown> | undefined
        return e && `${k}_nombre` in e ? e[`${k}_nombre`] : p[`${k}_nombre`]
      },
      true,
    )
    if (opciones.length > 0) out[k] = opciones
  }
  return out
}

/**
 * Filtro de opciones para `cmdk` **insensible a acentos/diacríticos**. El filtro
 * por defecto de cmdk solo pasa a minúsculas y colapsa espacios/guiones, pero NO
 * quita acentos, así que buscar "jose" no encontraba "José" ni "pena" a "Peña"
 * (los combos con nombres/razones sociales acentuados parecían "no filtrar").
 * Normaliza ambos lados (NFD sin marcas combinantes) y hace match por substring:
 * devuelve 1 si el término está contenido en el valor, 0 si no (cmdk muestra > 0).
 */
export function filtrarOpcionSinAcentos(value: string, search: string): number {
  const norm = (s: string) =>
    s.toLocaleLowerCase('es').normalize('NFD').replace(/[̀-ͯ]/g, '').trim()
  return norm(value).includes(norm(search)) ? 1 : 0
}

/**
 * Pares {value: id, label: "nombre apellido"} de los participantes registrados,
 * para poblar el dropdown del filtro "Dependiente". El value es el `id` del
 * participante (lo que el upstream espera en `participante_id`); el label es su
 * nombre completo. Se descartan los que no tienen id y se ordena por nombre.
 */
export function participantePairs(
  participantes: Array<Record<string, unknown>>,
): FieldOption[] {
  return collectPairs(
    participantes,
    (p) => p.id,
    (p) => [p.nombre, p.apellido].filter(Boolean).join(' ') || null,
  )
}

/**
 * Descompone un valor de filtro multi-selección (string coma-separado, ej.
 * `"15,16,17"`) en la lista de sus valores individuales, descartando vacíos.
 * Un valor único sin comas devuelve `["15"]`; `undefined`/'' devuelve `[]`.
 * Es la contraparte de `joinMulti` y refleja el formato que el backend expande
 * a parámetros repetidos (ver `_split_multivalor`).
 */
export function parseMulti(v: string | undefined): string[] {
  if (!v) return []
  return v.split(',').filter((s) => s !== '')
}

/**
 * Une una lista de valores en el string coma-separado que consume el filtro.
 * Devuelve `undefined` cuando la lista queda vacía (para borrar el filtro).
 */
export function joinMulti(vals: string[]): string | undefined {
  const limpios = vals.filter((s) => s !== '')
  return limpios.length > 0 ? limpios.join(',') : undefined
}
